import React, { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import useUser from "../../hooks/useUser";
import FullPlayer from "./components/FullPlayer";
import HelpDrawer from "./components/HelpDrawer";
import {
  cardVisible,
  EMPTY_HELP_STATE,
  HelpState,
  isUnseenNew,
  loadHelpState,
  MAX_CARD_VISITS,
  progressKey,
  saveHelpState,
  todayKey,
} from "./helpStorage";
import { isOnPage } from "./helpUtils";
import { getHelpVideos } from "./services";
import { HelpApi, HelpVideo, HelpVideoList, PlayOptions } from "./types";

const HELP_PARAM = "help";
const TIME_PARAM = "t";
/** Only English videos exist so far; other locales come with the admin upload flow. */
const HELP_LOCALE = "en";
const EMPTY_LIST: HelpVideoList = { videos: [], settingHelp: {}, total: 0, available: 0, modules: [] };
/**
 * Poster links are signed for an hour (api-server helpVideos.urlTtlSeconds); reload the page's list
 * before they expire so a long-open tab keeps its thumbnails.
 */
const LIST_REFRESH_MS = 50 * 60 * 1000;

interface Playback extends PlayOptions {
  video: HelpVideo;
}

/** HelpApi plus what the Help components themselves need. */
interface HelpInternal extends HelpApi {
  hasUnseenNewHere: boolean;
  /** Every video the user may see; 0 hides the Help button. */
  available: number;
}

const HelpContext = createContext<HelpInternal | null>(null);

// Outside the provider (404 page, error boundary, embeds) Help is simply absent: no videos, no-op actions.
const NO_HELP: HelpInternal = {
  videos: [],
  openDrawer: () => undefined,
  play: () => undefined,
  playSetting: () => undefined,
  hasSettingHelp: () => false,
  cardVideoFor: () => undefined,
  markCardShown: () => undefined,
  later: () => undefined,
  dismiss: () => undefined,
  hasUnseenNewHere: false,
  available: 0,
};

export const useHelpInternal = (): HelpInternal => useContext(HelpContext) ?? NO_HELP;

export const useHelp = (): HelpApi => useHelpInternal();

export const HelpProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { currentUser, selectedBusiness } = useUser();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = currentUser?.id;
  const businessId = selectedBusiness?.id;

  const [list, setList] = useState<HelpVideoList>(EMPTY_LIST);
  const [state, setState] = useState<HelpState>(EMPTY_HELP_STATE);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [playback, setPlayback] = useState<Playback | null>(null);
  // Ids whose visit was already counted this session, so the 3rd count doesn't hide the card mid-visit.
  const countedThisSession = useRef(new Set<string>());

  useEffect(() => {
    if (userId !== undefined) setState(loadHelpState(userId));
  }, [userId]);

  // Only this page's videos are loaded; the library is paged in the drawer. The server filters by the
  // user's permissions in the selected business, so reload on a switch. Help is optional: on failure
  // there are simply no videos (the Help button hides itself).
  useEffect(() => {
    if (userId === undefined || businessId === undefined) return undefined;
    let active = true;
    // The previous page's videos don't belong here; the counts stay so the Help button doesn't blink.
    const clearPage = (current: HelpVideoList): HelpVideoList => ({ ...current, videos: [], settingHelp: {} });
    setList(clearPage);
    const load = () =>
      getHelpVideos({ locale: HELP_LOCALE, path: pathname })
        .then((next) => active && setList(next))
        .catch(() => active && setList(clearPage));
    void load();
    const timer = window.setInterval(load, LIST_REFRESH_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [userId, businessId, pathname]);

  const update = useCallback(
    (change: (current: HelpState) => HelpState) =>
      setState((current) => {
        const next = change(current);
        if (userId !== undefined) saveHelpState(userId, next);
        return next;
      }),
    [userId]
  );

  const { videos, settingHelp, available } = list;
  const findVideo = useCallback((id: string) => videos.find((video) => video.id === id), [videos]);

  const playVideo = useCallback((video: HelpVideo, options: PlayOptions = {}) => setPlayback({ video, ...options }), []);

  // A video of another page (a shared link) is looked up by id.
  const play = useCallback(
    (videoId: string, options: PlayOptions = {}) => {
      const known = findVideo(videoId);
      if (known) {
        playVideo(known, options);
        return;
      }
      getHelpVideos({ locale: HELP_LOCALE, id: videoId, limit: 1 })
        .then(({ videos: [found] }) => found && playVideo(found, options))
        .catch(() => undefined);
    },
    [findVideo, playVideo]
  );

  const playSetting = useCallback(
    (settingKey: string) => {
      const setting = settingHelp[settingKey];
      if (setting) play(setting.video, { start: setting.start, end: setting.end });
    },
    [play, settingHelp]
  );

  // ?help=<videoId>&t=<seconds> opens the full player once, then leaves the URL clean.
  useEffect(() => {
    const id = searchParams.get(HELP_PARAM);
    if (!id || userId === undefined || businessId === undefined) return;
    const start = Number(searchParams.get(TIME_PARAM));
    play(id, { start: Number.isFinite(start) ? start : 0 });
    const next = new URLSearchParams(searchParams);
    next.delete(HELP_PARAM);
    next.delete(TIME_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, play, userId, businessId]);

  const saveProgress = useCallback(
    (video: HelpVideo, seconds: number) =>
      update((current) => ({ ...current, progress: { ...current.progress, [progressKey(video)]: seconds } })),
    [update]
  );

  const effectiveState = useCallback(
    (video: HelpVideo): HelpState =>
      countedThisSession.current.has(video.id)
        ? { ...state, cardShown: { ...state.cardShown, [video.id]: (state.cardShown[video.id] ?? 0) - 1 } }
        : state,
    [state]
  );

  const api = useMemo<HelpInternal>(
    () => ({
      videos,
      openDrawer: () => setDrawerOpen(true),
      play,
      playSetting,
      hasSettingHelp: (settingKey) => settingKey in settingHelp && !!findVideo(settingHelp[settingKey].video),
      cardVideoFor: (path) =>
        videos.find((video) => isOnPage(video, path) && cardVisible(video, effectiveState(video), todayKey())),
      markCardShown: (videoId) => {
        if (countedThisSession.current.has(videoId)) return;
        countedThisSession.current.add(videoId);
        update((current) => ({
          ...current,
          cardShown: {
            ...current.cardShown,
            [videoId]: Math.min(MAX_CARD_VISITS, (current.cardShown[videoId] ?? 0) + 1),
          },
        }));
      },
      later: (videoId) => update((current) => ({ ...current, laterOn: { ...current.laterOn, [videoId]: todayKey() } })),
      dismiss: (videoId) => update((current) => ({ ...current, dismissed: { ...current.dismissed, [videoId]: true } })),
      hasUnseenNewHere: videos.some((video) => isOnPage(video, pathname) && isUnseenNew(video, state, todayKey())),
      available,
    }),
    [videos, settingHelp, available, play, playSetting, findVideo, effectiveState, update, pathname, state]
  );

  const playing = playback?.video;
  const close = (seconds: number) => {
    if (playing) saveProgress(playing, seconds);
    setPlayback(null);
  };

  return (
    <HelpContext.Provider value={api}>
      {children}
      <HelpDrawer
        open={drawerOpen}
        videos={videos}
        available={available}
        locale={HELP_LOCALE}
        state={state}
        onClose={() => setDrawerOpen(false)}
        onPlay={playVideo}
      />
      {playing && (
        <FullPlayer
          key={`${playing.id}-${playback?.start}-${playback?.end}`}
          video={playing}
          start={playback?.start}
          end={playback?.end}
          onProgress={(seconds) => saveProgress(playing, seconds)}
          onClose={close}
        />
      )}
    </HelpContext.Provider>
  );
};
